import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {isSnapshotPeriodIncomplete, snapshotHasPublishedDays, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function topProductsTable(products, renderNumber, renderCurrencyValue, t) {
    return <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
            <thead>
            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.products.product", {defaultValue: "Produkt"})}</th>
                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.products.quantity", {defaultValue: "Kusy"})}</th>
                <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
            </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {products.map((product) => <tr key={`${product.productName}-${product.amount}-${moneyAmount(product.turnover)}`}>
                <td className="max-w-[36rem] py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                    <div className="truncate">{product.productName}</div>
                </td>
                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(product.amount)}</td>
                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.turnover, true)}</td>
            </tr>)}
            </tbody>
        </table>
    </div>;
}

export default function ReportingCategoriesBrandsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const productAssortmentPerformanceFilter = useMemo(() => ({
        ...customerWholesaleRequestFilter,
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        limit: 5000,
        page: 1,
        mode: "net",
        sortBy: "turnover",
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, customerWholesaleRequestFilter]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        new ErpCalculationApi(getAuthorityConfiguration())
            .productAssortmentPerformance({
                projectId: project.id,
                productAssortmentPerformanceFilter,
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, productAssortmentPerformanceFilter]);

    const productPublication = data?.productPublication;
    const productPublicationIncomplete = isSnapshotPeriodIncomplete(productPublication);
    const products = snapshotRowsForPublishedDays(data?.products ?? [], productPublication);
    const totalItems = Number(data?.totalItems ?? products.length);
    const isTruncated = totalItems > products.length;
    const unclassifiedProductsMeta = productPublicationIncomplete
        ? undefined
        : isTruncated
            ? t("reporting_lab.abc.loaded_partial_scope", {defaultValue: "Zobrazeno {{loaded}} z {{total}} produktů", loaded: products.length, total: totalItems})
            : renderPercentValue(products.length > 0 ? 100 : 0);
    const currency = products.find((product) => product.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = products.reduce((sum, product) => sum + moneyAmount(product.turnover), 0);
    const totalAmount = products.reduce((sum, product) => sum + Number(product.amount ?? 0), 0);
    const unclassifiedName = t("reporting_lab.categories_brands.unclassified", {defaultValue: "Nezařazeno"});
    const categoryRows = products.length > 0 ? [{
        name: unclassifiedName,
        productCount: products.length,
        amount: totalAmount,
        turnover: totalTurnover / 100,
    }] : [];
    const topProducts = products.slice(0, 12);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const rankingOption = (rows, name) => ({
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        yAxis: {
            type: "category",
            data: [...rows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 180, overflow: "truncate"},
        },
        series: [
            {
                name,
                type: "bar",
                data: [...rows].reverse().map((row, index) => ({
                    value: row.turnover,
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 28,
            },
        ],
    });

    const topProductsOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 70, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 0, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: topProducts.map((product) => product.productName),
            axisLabel: {fontSize: 12, interval: 0, rotate: 35, width: 100, overflow: "truncate"},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: [
            {
                name: t("reporting_lab.series.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: topProducts.map((product, index) => ({
                    value: moneyToMajor(product.turnover),
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 28,
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.categories_brands", {defaultValue: "Kategorie a značky"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                className="col-span-12"
                title={t("reporting_lab.categories_brands.explanation_title", {defaultValue: "K čemu slouží pohled podle kategorií a značek"})}
            >
                {t("reporting_lab.categories_brands.explanation", {defaultValue: "Pohled ukazuje, které části sortimentu táhnou obrat a kde se ztrácí výkon. Obrat a kusy vycházejí z dokončených objednávek. Aktuální produktová agregace zatím nenese atributy kategorie a značky, proto zde současně vidíme rozsah nezařazených produktů a připravený cílový layout."})}
            </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
            <ReportingSnapshotStatus snapshots={productPublication}/>

            <div className="col-span-12 md:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.categories_brands.products", {defaultValue: "Produkty v datech"})} value={productPublicationIncomplete ? "—" : renderNumber(totalItems)} meta={productPublicationIncomplete ? snapshotUnavailableShort(productPublication, t) : isTruncated ? t("reporting_lab.abc.loaded_partial_scope", {defaultValue: "Zobrazeno {{loaded}} z {{total}} produktů", loaded: products.length, total: totalItems}) : t("reporting_lab.abc.full_scope", {defaultValue: "Celý sortiment dle aktuálních filtrů"})}/>
            </div>
            <div className="col-span-12 md:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.categories_brands.unclassified_categories", {defaultValue: "Bez kategorie"})} value={productPublicationIncomplete ? "—" : renderNumber(products.length)} meta={unclassifiedProductsMeta} tone={productPublicationIncomplete ? "default" : products.length > 0 ? "warn" : "good"}/>
            </div>
            <div className="col-span-12 md:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.categories_brands.unclassified_brands", {defaultValue: "Bez značky"})} value={productPublicationIncomplete ? "—" : renderNumber(products.length)} meta={unclassifiedProductsMeta} tone={productPublicationIncomplete ? "default" : products.length > 0 ? "warn" : "good"}/>
            </div>
            <div className="col-span-12 md:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.categories_brands.unclassified_turnover", {defaultValue: "Obrat nezařazených"})} value={productPublicationIncomplete ? "—" : renderCurrencyValue({amount: totalTurnover, currency}, true)} meta={productPublicationIncomplete ? snapshotUnavailableShort(productPublication, t) : isTruncated ? t("reporting_lab.abc.loaded_partial_scope", {defaultValue: "Zobrazeno {{loaded}} z {{total}} produktů", loaded: products.length, total: totalItems}) : t("reporting_lab.categories_brands.needs_mapping", {defaultValue: "Čeká na produktové mapování"})}/>
            </div>

            {!productPublicationIncomplete && <Panel loading={loading} title={t("reporting_lab.categories_brands.categories_by_revenue", {defaultValue: "Stav kategoriového mapování"})} className="col-span-12 xl:col-span-6">
                <EChart option={rankingOption(categoryRows, t("reporting_lab.categories_brands.category_revenue", {defaultValue: "Obrat bez kategorie"}))} className="h-80"/>
            </Panel>}
            {!productPublicationIncomplete && <Panel loading={loading} title={t("reporting_lab.categories_brands.brands_by_revenue", {defaultValue: "Stav značkového mapování"})} className="col-span-12 xl:col-span-6">
                <EChart option={rankingOption(categoryRows, t("reporting_lab.categories_brands.brand_revenue", {defaultValue: "Obrat bez značky"}))} className="h-80"/>
            </Panel>}

            <Panel loading={loading} title={t("reporting_lab.categories_brands.top_unclassified_products", {defaultValue: "Top produkty bez kategorie a značky"})} className="col-span-12 xl:col-span-7">
                {productPublicationIncomplete && !snapshotHasPublishedDays(productPublication)
                    ? <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"})}</div>
                    : topProductsTable(topProducts, renderNumber, renderCurrencyValue, t)}
            </Panel>
            {!productPublicationIncomplete && <Panel loading={loading} title={t("reporting_lab.categories_brands.product_revenue_base", {defaultValue: "Obratový základ produktů"})} className="col-span-12 xl:col-span-5">
                <EChart option={topProductsOption} className="h-96"/>
            </Panel>}
        </div>
    </WideSecureLayout>;
}
